/**
 * Corrections people have made to building records.
 *
 * Denver's outlines carry a surveyed height and a coarse type — "Residential",
 * "Commercial" — and nothing else. OpenStreetMap fills in some of the rest,
 * unevenly. So for most buildings downtown, almost everything you would want to
 * know is simply missing, and the only way it gets there is somebody going and
 * looking.
 *
 * Anchoring, and why it is by position: the building features have no stable
 * identifier. Denver's export gives each footprint a height and a type, no id,
 * and the array index shifts whenever the extract is regenerated. A centroid
 * does not shift — the building is where it is — so an edit is stored at its
 * footprint centre and matched back by proximity.
 *
 * Edits never overwrite the survey. They are shown as a separate, attributed
 * layer of claims on top of it: "Denver says 38m, Sam says 11 floors" is two
 * facts, not a contradiction to be resolved.
 */

/** How close a stored edit must be to a centroid to be the same building. */
const MATCH_RADIUS_M = 25;

/** Cell size for the lookup grid, in degrees — comfortably over the radius. */
const CELL = 0.0005;

const cellKey = (lng, lat) =>
  `${Math.round(lng / CELL)}:${Math.round(lat / CELL)}`;

/** Metres between two lng/lat pairs, flat-earth. Fine at city scale. */
function metresBetween([lng1, lat1], [lng2, lat2]) {
  const dx = (lng1 - lng2) * 85600;
  const dy = (lat1 - lat2) * 110540;
  return Math.hypot(dx, dy);
}

/**
 * Centre of a building footprint.
 *
 * The outer ring only: interior rings are courtyards, and including them pulls
 * the centre toward a hole in the building.
 */
export function centroidOf(polygon) {
  const ring = Array.isArray(polygon?.[0]?.[0]) ? polygon[0] : polygon;
  if (!Array.isArray(ring) || ring.length === 0) return null;
  let x = 0;
  let y = 0;
  let n = 0;
  for (const point of ring) {
    if (!Array.isArray(point)) continue;
    x += point[0];
    y += point[1];
    n += 1;
  }
  return n ? [x / n, y / n] : null;
}

export class BuildingEdits {
  constructor({ apiBase = "./api" } = {}) {
    this.apiBase = apiBase;
    this.edits = [];
    this.canWrite = true;
    this._grid = new Map();
  }

  get count() {
    return this.edits.length;
  }

  async load() {
    try {
      const response = await fetch(`${this.apiBase}/contributions?kind=building`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this.edits = Array.isArray(body) ? body : [];
    } catch (error) {
      // Corrections are an addition to the map, never a dependency of it.
      this.edits = [];
      console.warn("building edits unavailable:", error.message);
    }
    this.reindex();
    return this.edits;
  }

  reindex() {
    this._grid = new Map();
    for (const edit of this.edits) {
      const key = cellKey(edit.lng, edit.lat);
      if (!this._grid.has(key)) this._grid.set(key, []);
      this._grid.get(key).push(edit);
    }
  }

  /** The edit recorded for this footprint, if anyone has made one. */
  find(centroid) {
    if (!centroid) return null;
    const [lng, lat] = centroid;
    const cx = Math.round(lng / CELL);
    const cy = Math.round(lat / CELL);

    let best = null;
    let bestDistance = Infinity;
    // Neighbouring cells too: a centroid near a cell edge would otherwise miss
    // an edit stored a metre away on the other side of it.
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (const edit of this._grid.get(`${cx + dx}:${cy + dy}`) ?? []) {
          const distance = metresBetween(centroid, [edit.lng, edit.lat]);
          if (distance < bestDistance) {
            best = edit;
            bestDistance = distance;
          }
        }
      }
    }
    return bestDistance <= MATCH_RADIUS_M ? best : null;
  }

  /** Create or overwrite the record for one building. */
  async save(centroid, fields) {
    const existing = this.find(centroid);
    const body = {
      kind: "building",
      lng: centroid[0],
      lat: centroid[1],
      label: fields.name ?? "",
      detail: fields.note ?? "",
      ...fields,
    };

    const url = existing
      ? `${this.apiBase}/contributions/${encodeURIComponent(existing.id)}`
      : `${this.apiBase}/contributions`;

    const response = await fetch(url, {
      method: existing ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (response.status === 401 || response.status === 403) {
      this.canWrite = false;
      throw new Error("Sign in to edit building information");
    }
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.error || `Could not save (${response.status})`);
    }

    const index = this.edits.findIndex((e) => e.id === payload.id);
    if (index >= 0) this.edits[index] = payload;
    else this.edits.push(payload);
    this.reindex();
    return payload;
  }

  async remove(centroid) {
    const existing = this.find(centroid);
    if (!existing) return;
    const response = await fetch(
      `${this.apiBase}/contributions/${encodeURIComponent(existing.id)}`,
      { method: "DELETE" });
    if (!response.ok) throw new Error(`Could not delete (${response.status})`);
    this.edits = this.edits.filter((e) => e.id !== existing.id);
    this.reindex();
  }
}
